/**
 * React glue for the dev panel: the session recorder (a gameplay-store
 * subscription feeding logTransition) and auto-play bound to the tap handler.
 */
import { useCallback, useEffect, useRef } from "react";

import {
  useGameplayStore,
  type GameplayState,
} from "../game/stores/useGameplayStore";
import { useAssetStore } from "../stores/useAssetStore";
import {
  AUTO_SPEEDS,
  runAutoPlay,
  type AutoGoal,
  type AutoState,
} from "./autoPlay";
import { useDevSession } from "./devSession";
import { nextBestMove } from "./bestMove";
import { joinGame, logTransition, type LogSnapshot } from "./sessionLog";

const snap = (s: GameplayState): LogSnapshot => s;

/** Records every game's moves into the dev session while mounted. */
export function useSessionRecorder(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return undefined;
    const joined = joinGame(
      useDevSession.getState().games,
      snap(useGameplayStore.getState()),
      Date.now(),
    );
    useDevSession.setState({ games: joined });
    return useGameplayStore.subscribe((next, prev) => {
      const dev = useDevSession.getState();
      const games = logTransition(dev.games, snap(prev), snap(next), {
        now: Date.now(),
        graph: useAssetStore.getState().graphData,
        source: dev.autoRunning ? "auto" : "tap",
      });
      if (games !== dev.games) useDevSession.setState({ games });
    });
  }, [enabled]);
}

export function autoState(): AutoState {
  const s = useGameplayStore.getState();
  return {
    status: s.gameStatus,
    currentWord: s.currentWord,
    targetWord: s.targetWord,
    goldPath: s.optimalPath,
    localOptimalRoute: s.suggestedPathFromCurrent,
    graph: useAssetStore.getState().graphData ?? {},
  };
}

/** The next best move while "show best move" is on, else null. */
export function useShownBestNext(): string | null {
  const show = useDevSession((s) => s.showBestMove);
  const current = useGameplayStore((s) => s.currentWord);
  const target = useGameplayStore((s) => s.targetWord);
  const goldPath = useGameplayStore((s) => s.optimalPath);
  const localOptimalRoute = useGameplayStore((s) => s.suggestedPathFromCurrent);
  const status = useGameplayStore((s) => s.gameStatus);
  if (!show || status !== "playing") return null;
  return nextBestMove({
    currentWord: current,
    targetWord: target,
    goldPath,
    localOptimalRoute,
  });
}

/** Wait at least a frame (so React has re-rendered the board), then `ms`. */
const afterRender = (ms: number) =>
  new Promise<void>((resolve) => {
    const raf =
      globalThis.requestAnimationFrame ??
      ((f: () => void) => setTimeout(f, 16));
    raf(() => setTimeout(resolve, ms));
  });

/**
 * Auto-play through `move` (the board's tap handler). One run at a time;
 * `stop` cancels between moves.
 */
export function useAutoPlay(move: ((word: string) => void) | undefined) {
  const cancel = useRef(false);
  // The tap handler closes over the render's game state: always call the
  // latest one, never the one captured when the run started.
  const moveRef = useRef(move);
  moveRef.current = move;
  const running = useDevSession((s) => s.autoRunning);
  useEffect(
    () => () => {
      cancel.current = true;
    },
    [],
  );
  const run = useCallback(async (goal: AutoGoal) => {
    const dev = useDevSession.getState();
    if (!moveRef.current || dev.autoRunning) return 0;
    dev.markUsed();
    cancel.current = false;
    dev.set({ autoRunning: true });
    try {
      return await runAutoPlay({
        goal,
        getState: autoState,
        move: (w) => moveRef.current?.(w),
        wait: afterRender,
        delayMs: AUTO_SPEEDS[useDevSession.getState().speed],
        cancelled: () => cancel.current,
      });
    } finally {
      useDevSession.getState().set({ autoRunning: false });
    }
  }, []);
  const stop = useCallback(() => {
    cancel.current = true;
  }, []);
  return { run, stop, running };
}
